import { useState, useEffect, useCallback } from 'react';
import api from '@/api';
import { ArrowsClockwise, Plus, ShieldCheck, UserCircle, ListBullets, Trash, Clock, ArrowLeft, MagnifyingGlass, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { formatLabel, timeAgo, BADGE } from '@/lib/format';

export default function DataSourcesPage() {
  const [tab, setTab] = useState('sources');
  useEffect(() => { document.title = "Radar — Data Sources"; }, []);
  const [sources, setSources] = useState([]);
  const [watchlists, setWatchlists] = useState([]);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingSource, setSyncingSource] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceHistory, setSourceHistory] = useState([]);
  const [showCreateWL, setShowCreateWL] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(null);
  const [sanctionsStats, setSanctionsStats] = useState(null);
  const [pepStats, setPepStats] = useState(null);
  // 5A: Record browser
  const [browseSource, setBrowseSource] = useState(null);
  const [browseRecords, setBrowseRecords] = useState([]);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browsePage, setBrowsePage] = useState(0);
  const [browseSearch, setBrowseSearch] = useState('');
  const [syncConfig, setSyncConfig] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  // Source registry
  const [registryStats, setRegistryStats] = useState(null);
  const [registryGrouped, setRegistryGrouped] = useState({});

  const fetchSources = useCallback(async () => {
    try {
      // Fast path: load sources & watchlists first so the list renders immediately.
      const [s, w] = await Promise.all([api.get('/data-sources'), api.get('/watchlists')]);
      setSources(s.data.sources);
      setWatchlists(w.data.watchlists);
    } catch (e) { }
    // Stats are heavy aggregations on 1M+ docs — fire-and-forget in the background.
    api.get('/sanctions/stats').then(r => setSanctionsStats(r.data)).catch(() => {});
    api.get('/pep/stats').then(r => setPepStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchSources(); }, [fetchSources]);
  useEffect(() => {
    api.get('/source-registry/stats').then(r => setRegistryStats(r.data)).catch(() => {});
    api.get('/source-registry').then(r => setRegistryGrouped(r.data.grouped || {})).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === 'schedule') {
      api.get('/sync-engine/config').then(r => setSyncConfig(r.data)).catch(() => {});
      api.get('/audit', { params: { entity_type: 'data_source', limit: 20 } }).then(r => setSyncLogs(r.data.logs)).catch(() => {});
    }
  }, [tab]);

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try { await api.post('/data-sources/sync-all'); alert('Sync started for all sources.'); setTimeout(fetchSources, 5000); } catch (e) { alert('Failed'); }
    setSyncingAll(false);
  };

  const handleSyncSource = async (sourceId) => {
    setSyncingSource(sourceId);
    try { await api.post(`/data-sources/${sourceId}/sync`); alert(`Sync started for ${sourceId}.`); setTimeout(fetchSources, 5000); } catch (e) { alert('Failed'); }
    setSyncingSource(null);
  };

  const loadHistory = async (sourceId) => {
    setSelectedSource(sourceId);
    try { const r = await api.get(`/data-sources/${sourceId}/history`); setSourceHistory(r.data.logs); } catch { setSourceHistory([]); }
  };

  const handleCreateWatchlist = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try { await api.post('/watchlists', { name: fd.get('name'), description: fd.get('description') }); setShowCreateWL(false); fetchSources(); } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleAddEntry = async (wlId, e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const aliases = fd.get('aliases') ? fd.get('aliases').split(',').map(a => a.trim()) : [];
    try { await api.post(`/watchlists/${wlId}/entries`, { name: fd.get('name'), entity_type: fd.get('entity_type'), reason: fd.get('reason'), aliases }); setShowAddEntry(null); fetchSources(); } catch (err) { alert('Error'); }
  };

  const handleDeleteWL = async (id) => { if (window.confirm('Delete?')) { await api.delete(`/watchlists/${id}`); fetchSources(); } };
  const handleRemoveEntry = async (wlId, entryId) => { await api.delete(`/watchlists/${wlId}/entries/${entryId}`); fetchSources(); };

  // 5A: Record browser - map source_id to DB source field names
  const SOURCE_DB_NAMES = {
    'OFAC_SDN': 'OFAC SDN',
    'ofac_sdn': 'OFAC SDN',
    'UN_CONSOLIDATED': 'UN Consolidated',
    'un_consolidated': 'UN Consolidated',
    'UK_OFSI': 'UK OFSI',
    'uk_ofsi': 'UK OFSI',
    'EU_SANCTIONS': 'EU Sanctions',
    'eu_sanctions': 'EU Sanctions',
    'OFAC_NON_SDN': 'OFAC Non-SDN',
    'ofac_non_sdn': 'OFAC Non-SDN',
    'FBI_MOST_WANTED': 'FBI Most Wanted',
    'fbi_wanted': 'FBI Most Wanted',
    'fbi_most_wanted': 'FBI Most Wanted',
    'INTERPOL_RED': 'Interpol Red Notice',
    'interpol_red': 'Interpol Red Notice',
    'opensanctions_sanctions': 'OpenSanctions Global Sanctions (Aggregated)',
    'opensanctions_crime': 'OpenSanctions Crime & Wanted Lists',
    'opensanctions_debarment': 'OpenSanctions Debarment Lists',
  };
  const openBrowser = async (source) => {
    setBrowseSource(source);
    setBrowsePage(0);
    setBrowseSearch('');
    await loadRecords(source, 0, '');
  };
  const loadRecords = async (source, page, search) => {
    try {
      // Route PEP-type sources to /pep; everything else to /sanctions.
      // Use the registered list_type when available (robust to lowercase / uppercase ids).
      const listType = (source.list_type || '').toLowerCase();
      const sidLc = (source.source_id || '').toLowerCase();
      const isPEP = listType === 'pep' || sidLc === 'pep_opensanctions' || sidLc === 'wikidata_politicians';
      const params = { skip: page * 50, limit: 50 };
      if (search) params.search = search;
      if (isPEP) {
        // For PEP, filter by source_id so each source shows only its own records.
        if (source.source_id) params.source = source.source_id;
      } else {
        const dbName = SOURCE_DB_NAMES[source.source_id];
        if (dbName) params.source = dbName;
      }
      const endpoint = isPEP ? '/pep' : '/sanctions';
      const r = await api.get(endpoint, { params });
      setBrowseRecords(r.data.entries || []);
      setBrowseTotal(r.data.total || 0);
    } catch { setBrowseRecords([]); setBrowseTotal(0); }
  };
  const handleBrowseSearch = () => { setBrowsePage(0); loadRecords(browseSource, 0, browseSearch); };
  const handleBrowsePage = (p) => { setBrowsePage(p); loadRecords(browseSource, p, browseSearch); };

  return (
    <div data-testid="data-sources-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0A]">Data Sources</h1>
          <p className="text-sm text-[#52525B]">Manage sanctions databases, PEP data, and custom watchlists</p>
        </div>
        <button onClick={handleSyncAll} disabled={syncingAll} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] disabled:opacity-50">
          <ArrowsClockwise size={16} className={syncingAll ? 'animate-spin' : ''} /> {syncingAll ? 'Syncing...' : 'Sync All Sources'}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck size={20} className="text-[#0052FF]" /><span className="text-xs uppercase tracking-[0.15em] font-medium text-[#52525B]">Active Sources</span></div>
          <p className="text-3xl font-bold text-green-600">{registryStats?.active_sources || sources.length}</p>
          <p className="text-xs text-[#A1A1AA] mt-1">of {registryStats?.total_sources || 0} registered ({Object.values(registryGrouped).flat().filter(s => s.coverage_status === 'covered_via_opensanctions').length} via OpenSanctions)</p>
        </div>
        <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
          <div className="flex items-center gap-2 mb-2"><UserCircle size={20} className="text-[#34C759]" /><span className="text-xs uppercase tracking-[0.15em] font-medium text-[#52525B]">Unique Entities</span></div>
          <p className="text-3xl font-bold">{registryStats?.dedup?.unique_entities?.toLocaleString() || 0}</p>
          <p className="text-xs text-[#A1A1AA] mt-1">{registryStats?.dedup?.total_records?.toLocaleString() || 0} total records ({registryStats?.dedup?.dedup_ratio || 0}% dedup)</p>
        </div>
        <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck size={20} className="text-[#FF9500]" /><span className="text-xs uppercase tracking-[0.15em] font-medium text-[#52525B]">Multi-List Entities</span></div>
          <p className="text-3xl font-bold">{registryStats?.dedup?.multi_source_3plus || 0}</p>
          <p className="text-xs text-[#A1A1AA] mt-1">listed on 3+ sources</p>
        </div>
        <div className="bg-white border border-[#E4E4E7] rounded-md p-5">
          <div className="flex items-center gap-2 mb-2"><ListBullets size={20} className="text-[#52525B]" /><span className="text-xs uppercase tracking-[0.15em] font-medium text-[#52525B]">Custom Watchlists</span></div>
          <p className="text-3xl font-bold">{watchlists.length}</p>
          <p className="text-xs text-[#A1A1AA] mt-1">{watchlists.reduce((a, w) => a + (w.entries?.length || 0), 0)} entries</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E4E4E7]">
        {[{ id: 'sources', label: 'Active Sources' }, { id: 'registry', label: `All Sources (${registryStats?.total_sources || 0})` }, { id: 'watchlists', label: 'Custom Watchlists' }, { id: 'schedule', label: 'Sync Schedule' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${tab === t.id ? 'border-[#0052FF] text-[#0052FF]' : 'border-transparent text-[#52525B] hover:text-[#0A0A0A]'}`}>{t.label}</button>
        ))}
      </div>

      {/* OFFICIAL SOURCES */}
      {tab === 'sources' && (
        <div className="space-y-3">
          {sources.map(s => (
            <div key={s.source_id} className="bg-white border border-[#E4E4E7] rounded-md p-4 cursor-pointer hover:shadow-md transition-all" onClick={() => openBrowser(s)}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${s.status === 'completed' && s.last_synced && (Date.now() - new Date(s.last_synced).getTime()) < 48*3600000 ? 'bg-green-500' : 'bg-red-500'}`} />
                    <h3 className="text-sm font-semibold text-[#0A0A0A]">{s.name}</h3>
                  </div>
                  <p className="text-xs text-[#52525B] mt-0.5">{s.authority}</p>
                  <div className="flex gap-4 mt-2 text-xs">
                    <span className="text-[#0A0A0A] font-bold">{s.entries_count?.toLocaleString() || 0} entries</span>
                    <span className={s.status === 'completed' ? 'text-green-600' : s.status === 'failed' ? 'text-red-600' : 'text-[#A1A1AA]'}>{formatLabel(s.status || 'never synced')}</span>
                    {s.last_synced && <span className="text-[#A1A1AA]"><Clock size={12} className="inline mr-0.5" />Last: {timeAgo(s.last_synced)}</span>}
                  </div>
                </div>
                <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                  <button onClick={() => loadHistory(s.source_id)} className="px-2.5 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">History</button>
                  <button onClick={() => handleSyncSource(s.source_id)} disabled={syncingSource === s.source_id} className="px-2.5 py-1 text-xs bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] disabled:opacity-50">
                    {syncingSource === s.source_id ? 'Syncing...' : 'Sync'}
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* History Panel */}
          {selectedSource && (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">Sync History: {selectedSource}</h3>
                <button onClick={() => setSelectedSource(null)} className="text-xs text-[#A1A1AA] hover:text-[#0A0A0A]">Close</button>
              </div>
              {sourceHistory.length === 0 ? (
                <p className="text-xs text-[#A1A1AA]">No sync history available.</p>
              ) : (
                <table className="w-full">
                  <thead><tr className="border-b border-[#E4E4E7]">
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-[#52525B]">Started</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-[#52525B]">Completed</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-[#52525B]">Entries</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-[#52525B]">Status</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium text-[#52525B]">Duration</th>
                  </tr></thead>
                  <tbody>
                    {sourceHistory.map((h, i) => (
                      <tr key={i} className="border-b border-[#F0F0F1]">
                        <td className="px-3 py-2 text-xs">{h.started_at ? new Date(h.started_at).toLocaleString() : '-'}</td>
                        <td className="px-3 py-2 text-xs">{h.completed_at ? new Date(h.completed_at).toLocaleString() : '-'}</td>
                        <td className="px-3 py-2 text-xs font-medium">{h.entries_count?.toLocaleString() || '-'}</td>
                        <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${h.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{h.status}</span></td>
                        <td className="px-3 py-2 text-xs text-[#A1A1AA]">{h.duration_seconds ? `${h.duration_seconds.toFixed(1)}s` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}


      {/* REGISTRY TAB — All sources with 3-status display */}
      {tab === 'registry' && (
        <div>
          {/* Coverage bar */}
          {(() => {
            const total = registryStats?.total_sources || 1;
            const direct = Object.values(registryGrouped).flat().filter(s => s.coverage_status === 'direct_feed').length;
            const covered = Object.values(registryGrouped).flat().filter(s => s.coverage_status === 'covered_via_opensanctions').length;
            const registered = total - direct - covered;
            const pct = Math.round((direct + covered) / total * 100);
            return (
              <div className="bg-white border border-[#E4E4E7] rounded-md p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold">Source Coverage: {pct}%</span>
                  <button onClick={async () => { await api.post('/source-registry/sync-opensanctions'); alert('OpenSanctions bulk sync started.'); }} className="px-3 py-1.5 text-xs bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Sync OpenSanctions</button>
                </div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
                  <div className="h-full bg-green-500" style={{width: `${direct/total*100}%`}} />
                  <div className="h-full bg-blue-400" style={{width: `${covered/total*100}%`}} />
                </div>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> {direct} Direct Feeds</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" /> {covered} via OpenSanctions</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" /> {registered} Registered</span>
                </div>
              </div>
            );
          })()}

          {/* Grouped by type */}
          {Object.entries(registryGrouped).sort((a, b) => b[1].length - a[1].length).map(([type, srcs]) => {
            const directCount = srcs.filter(s => s.coverage_status === 'direct_feed').length;
            const coveredCount = srcs.filter(s => s.coverage_status === 'covered_via_opensanctions').length;
            const typePct = Math.round((directCount + coveredCount) / Math.max(srcs.length, 1) * 100);
            return (
            <details key={type} open={directCount > 0} className="mb-3">
              <summary className="cursor-pointer py-2 border-b border-[#E4E4E7] flex items-center justify-between">
                <span className="text-sm font-semibold">{type} ({srcs.length})</span>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-green-600">{directCount} direct</span>
                  <span className="text-blue-600">{coveredCount} covered</span>
                  <span className="text-[#A1A1AA]">{typePct}%</span>
                </div>
              </summary>
              <div className="mt-1 space-y-0.5">
                {srcs.sort((a, b) => {
                  const order = {direct_feed: 0, covered_via_opensanctions: 1, registered: 2};
                  return (order[a.coverage_status] || 2) - (order[b.coverage_status] || 2);
                }).map(s => (
                  <div key={s.source_id} className="flex items-center justify-between px-3 py-1 text-xs hover:bg-[#F7F7F8] rounded">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${s.coverage_status === 'direct_feed' ? 'bg-green-500' : s.coverage_status === 'covered_via_opensanctions' ? 'bg-blue-400' : 'bg-gray-300'}`} />
                      <span className={s.coverage_status === 'registered' ? 'text-[#A1A1AA]' : 'text-[#0A0A0A]'}>{s.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#A1A1AA]">{s.jurisdiction}</span>
                      {s.entries_count > 0 && <span className="font-medium text-[#52525B]">{s.entries_count.toLocaleString()}</span>}
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s.coverage_status === 'direct_feed' ? 'bg-green-50 text-green-700' : s.coverage_status === 'covered_via_opensanctions' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                        {s.coverage_status === 'direct_feed' ? 'Direct Feed' : s.coverage_status === 'covered_via_opensanctions' ? 'OpenSanctions' : 'Registered'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          );})}
        </div>
      )}


      {/* WATCHLISTS */}
      {tab === 'watchlists' && (
        <div>
          <div className="flex justify-end mb-3">
            <button onClick={() => setShowCreateWL(true)} className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-[#0052FF] text-white rounded hover:bg-[#0040CC]">
              <Plus size={14} /> Create Watchlist
            </button>
          </div>

          {watchlists.length === 0 ? (
            <div className="bg-white border border-[#E4E4E7] rounded-md p-8 text-center">
              <ListBullets size={40} className="mx-auto text-[#A1A1AA] mb-3" />
              <p className="text-sm text-[#52525B]">No custom watchlists</p>
              <p className="text-xs text-[#A1A1AA]">Create watchlists to add custom entries for screening</p>
            </div>
          ) : (
            <div className="space-y-3">
              {watchlists.map(wl => (
                <div key={wl.id} className="bg-white border border-[#E4E4E7] rounded-md p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold">{wl.name}</h3>
                      {wl.description && <p className="text-xs text-[#A1A1AA]">{wl.description}</p>}
                      <p className="text-xs text-[#52525B] mt-1">{wl.entries?.length || 0} entries • Used as data source for screening</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setShowAddEntry(wl.id)} className="px-2 py-1 text-[10px] bg-[#0052FF] text-white rounded hover:bg-[#0040CC]">Add Entry</button>
                      <button onClick={() => handleDeleteWL(wl.id)} className="px-2 py-1 text-[10px] text-red-600 border border-red-200 rounded hover:bg-red-50">Delete</button>
                    </div>
                  </div>
                  {wl.entries?.length > 0 && (
                    <div className="mt-2 border-t border-[#E4E4E7] pt-2">
                      {wl.entries.map(e => (
                        <div key={e.id} className="flex items-center justify-between py-1.5 text-xs">
                          <div><span className="font-medium">{e.name}</span><span className="text-[#A1A1AA] ml-2">{e.entity_type} {e.reason ? `• ${e.reason}` : ''}</span>{e.aliases?.length > 0 && <span className="text-[#A1A1AA] ml-1">Aliases: {e.aliases.join(', ')}</span>}</div>
                          <button onClick={() => handleRemoveEntry(wl.id, e.id)} className="text-red-400 hover:text-red-600"><Trash size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Create Watchlist Modal */}
          {showCreateWL && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowCreateWL(false)}>
              <div className="bg-white rounded-md border shadow-lg w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <h2 className="font-heading text-lg font-semibold mb-4">Create Watchlist</h2>
                <form onSubmit={handleCreateWatchlist} className="space-y-3">
                  <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Name</label><input name="name" required className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
                  <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Description</label><input name="description" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
                  <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowCreateWL(false)} className="px-4 py-2 text-sm border rounded-md hover:bg-[#F0F0F1]">Cancel</button><button type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Create</button></div>
                </form>
              </div>
            </div>
          )}

          {/* Add Entry Modal */}
          {showAddEntry && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowAddEntry(null)}>
              <div className="bg-white rounded-md border shadow-lg w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
                <h2 className="font-heading text-lg font-semibold mb-4">Add Entry</h2>
                <form onSubmit={e => handleAddEntry(showAddEntry, e)} className="space-y-3">
                  <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Name</label><input name="name" required className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Type</label><select name="entity_type" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md"><option value="individual">Individual</option><option value="entity">Entity</option></select></div>
                    <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Reason</label><input name="reason" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
                  </div>
                  <div><label className="text-xs uppercase tracking-wider text-[#52525B] font-medium">Aliases (comma-separated)</label><input name="aliases" className="w-full mt-1 px-3 py-2 text-sm border border-[#E4E4E7] rounded-md" /></div>
                  <div className="flex justify-end gap-2"><button type="button" onClick={() => setShowAddEntry(null)} className="px-4 py-2 text-sm border rounded-md hover:bg-[#F0F0F1]">Cancel</button><button type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Add</button></div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SYNC SCHEDULE TAB (Part 5) */}
      {tab === 'schedule' && syncConfig && (
        <div>
          <div className="bg-white border border-[#E4E4E7] rounded-md p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`w-3 h-3 rounded-full ${syncConfig.is_running ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              <span className="text-sm font-semibold">Sync Engine: {syncConfig.is_running ? 'Running' : 'Stopped'}</span>
              {syncConfig.last_run && <span className="text-xs text-[#A1A1AA]">Last run: {timeAgo(syncConfig.last_run)}</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={async () => { await api.post('/sync-engine/start'); const r = await api.get('/sync-engine/config'); setSyncConfig(r.data); }} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-md hover:bg-green-700">Start</button>
              <button onClick={async () => { await api.post('/sync-engine/stop'); const r = await api.get('/sync-engine/config'); setSyncConfig(r.data); }} className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700">Stop</button>
              <button onClick={async () => { await api.post('/sync-engine/run-now'); alert('Sync cycle triggered.'); }} className="px-3 py-1.5 text-xs bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Run All Now</button>
            </div>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden mb-6">
            <table className="w-full">
              <thead><tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]">
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Source</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Interval</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Last Synced</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Status</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Entries</th>
                <th className="px-4 py-2"></th>
              </tr></thead>
              <tbody>
                {Object.entries(syncConfig.sources || {}).map(([sid, cfg]) => {
                  const sidLc = (sid || '').toLowerCase();
                  const src = sources.find(s => (s.source_id || '').toLowerCase() === sidLc) || {};
                  return (
                    <tr key={sid} className="border-b border-[#E4E4E7]">
                      <td className="px-4 py-2 text-sm font-medium">{src.name || sid}</td>
                      <td className="px-4 py-2">
                        <select defaultValue={cfg.interval_hours || 24} onChange={async (e) => {
                          await api.put('/sync-engine/config', { sources: { ...syncConfig.sources, [sid]: { ...cfg, interval_hours: parseInt(e.target.value) } } });
                          const r = await api.get('/sync-engine/config'); setSyncConfig(r.data);
                        }} className="px-2 py-1 text-xs border border-[#E4E4E7] rounded-md">
                          {[1, 2, 4, 6, 12, 24, 48, 168, 720].map(h => <option key={h} value={h}>{h === 168 ? 'Weekly' : h === 720 ? 'Monthly' : `Every ${h}h`}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2 text-xs text-[#A1A1AA]">{src.last_synced ? timeAgo(src.last_synced) : 'Never'}</td>
                      <td className="px-4 py-2"><span className={`${BADGE} ${src.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' : src.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>{formatLabel(src.status || 'Never Synced')}</span></td>
                      <td className="px-4 py-2 text-sm font-medium">{src.entries_count?.toLocaleString() || 0}</td>
                      <td className="px-4 py-2"><button onClick={() => handleSyncSource(sid)} className="px-2 py-1 text-xs bg-[#0052FF] text-white rounded hover:bg-[#0040CC]">Sync</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 5A: Record Browser Modal */}
      {browseSource && (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setBrowseSource(null)}>
        <div className="bg-white rounded-md border border-[#E4E4E7] shadow-lg w-full max-w-5xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg font-semibold">{browseSource.name}</h2>
              <p className="text-xs text-[#52525B]">{browseSource.authority} — {browseTotal.toLocaleString()} entries</p>
            </div>
            <button onClick={() => setBrowseSource(null)} className="px-3 py-1.5 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">← Back to Data Sources</button>
          </div>
          <div className="px-6 py-3 border-b border-[#E4E4E7]">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input value={browseSearch} onChange={e => setBrowseSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleBrowseSearch()} placeholder="Search entries by name..." className="w-full pl-8 pr-3 py-2 text-sm border border-[#E4E4E7] rounded-md" />
              </div>
              <button onClick={handleBrowseSearch} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC]">Search</button>
            </div>
          </div>
          <div className="overflow-auto">
            <table className="w-full">
              <thead><tr className="border-b border-[#E4E4E7] bg-[#F7F7F8]">
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Name</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Type</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Nationality</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">{browseSource.source_id === 'PEP_OPENSANCTIONS' || browseSource.source_id === 'pep_opensanctions' ? 'Position' : 'Programme'}</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">Aliases</th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wider font-medium text-[#52525B]">{browseSource.source_id === 'PEP_OPENSANCTIONS' || browseSource.source_id === 'pep_opensanctions' ? 'Tier' : 'Designated'}</th>
              </tr></thead>
              <tbody>
                {browseRecords.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-[#F0F0F1] hover:bg-[#F7F7F8]">
                    <td className="px-4 py-2 text-sm font-medium">{r.name}</td>
                    <td className="px-4 py-2 text-xs">{formatLabel(r.entity_type || r.category || '')}</td>
                    <td className="px-4 py-2 text-xs">{r.nationality || r.country || ''}</td>
                    <td className="px-4 py-2 text-xs text-[#52525B]">{r.program || r.position || ''}</td>
                    <td className="px-4 py-2 text-xs text-[#A1A1AA]">{(r.aliases || []).slice(0, 3).join(', ')}</td>
                    <td className="px-4 py-2 text-xs">{r.designation_date || (r.tier ? `Tier ${r.tier}` : '')}</td>
                  </tr>
                ))}
                {browseRecords.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#A1A1AA]">No records found.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t border-[#E4E4E7] flex items-center justify-between">
            <span className="text-xs text-[#A1A1AA]">Showing {browsePage * 50 + 1}–{Math.min((browsePage + 1) * 50, browseTotal)} of {browseTotal.toLocaleString()}</span>
            <div className="flex gap-2">
              <button onClick={() => handleBrowsePage(Math.max(0, browsePage - 1))} disabled={browsePage === 0} className="px-3 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] disabled:opacity-40">Previous</button>
              <button onClick={() => handleBrowsePage(browsePage + 1)} disabled={(browsePage + 1) * 50 >= browseTotal} className="px-3 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}
